import { useId } from "react";
import { FormikContextType } from "formik";
import classNames from "classnames";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { CloseIcon, PlusIcon } from "@icons";
import { pluralize } from "@common/helpers/string";
import { ServiceTemplateRecurrencePeriod } from "@common/types/serviceTemplateRecurrencePeriod";
import recurrenceStyles from "@components/accounting/journalEntryDrawer/JournalEntryRecurrenceSelect.module.scss";
import type { ApplyCloseTemplateWizardValues } from "./applyCloseTemplate.schema";
import {
    DAY_OF_MONTH_OPTIONS,
    MONTH_OF_YEAR_LABELS,
    MONTH_OF_YEAR_VALUES,
    PERIOD_LABELS,
    PERIOD_UNIT_LABELS,
    QUARTER_MONTH_OPTIONS,
    SUPPORTED_SCHEDULE_PERIODS,
    WEEKDAY_LABELS,
    WEEKDAY_TOGGLE_OPTIONS,
} from "./serviceTemplateSchedule.constants";
import styles from "./ScheduleTemplateDrawer.module.scss";

const fields = createFormikFields<ApplyCloseTemplateWizardValues>();

interface Props {
    formik: FormikContextType<ApplyCloseTemplateWizardValues>;
}

interface DayOfMonthComboboxProps {
    value: number | null;
    placeholder?: string;
    onChange: (value: number | null) => void;
}

const PERIOD_ITEMS: Array<PickerItemData<ServiceTemplateRecurrencePeriod>> =
    SUPPORTED_SCHEDULE_PERIODS.map((period) => ({
        value: period,
        label: PERIOD_LABELS[period],
    }));

const MONTH_OF_YEAR_ITEMS: Array<PickerItemData<number>> =
    MONTH_OF_YEAR_VALUES.map((month) => ({
        value: month,
        label: MONTH_OF_YEAR_LABELS[month],
    }));

const DayOfMonthCombobox = ({
    value,
    placeholder,
    onChange,
}: DayOfMonthComboboxProps) => (
    <Picker.Select
        searchable
        items={DAY_OF_MONTH_OPTIONS}
        value={value}
        onValueChange={onChange}
        placeholder={placeholder}
        searchPlaceholder="Search days..."
        emptyLabel="No matching days"
        trigger={<Picker.Button variant="primary" size="xl" />}
    />
);

export const ScheduleTemplateStepServicePeriod = ({ formik }: Props) => {
    const startDateLabelId = useId();
    const intervalLabelId = useId();
    const schedule = formik.values.defaults.schedule;

    if (!schedule) {
        return null;
    }

    const { period } = schedule;
    const shouldShowWeekDays =
        period === ServiceTemplateRecurrencePeriod.WEEKLY;
    const shouldShowDatesOfMonth =
        period === ServiceTemplateRecurrencePeriod.MONTHLY;
    const shouldShowQuarterly =
        period === ServiceTemplateRecurrencePeriod.QUARTERLY;
    const shouldShowAnnually =
        period === ServiceTemplateRecurrencePeriod.ANNUALLY;
    // Switching period must clear the previous period's fields — otherwise
    // stale values (e.g. a `dateOfMonth` set while QUARTERLY) survive into a
    // MONTHLY submission, where `useCreateSchedulesForEntities` still derives
    // `monthRecurrenceType: DAY` and the backend `refineServiceStartPositioning`
    // rejects it, with no MONTHLY control able to clear it.
    const changePeriod = (
        nextPeriod: ServiceTemplateRecurrencePeriod | null,
    ) => {
        void formik.setFieldValue("defaults.schedule", {
            ...schedule,
            period: nextPeriod,
            weekDays: [],
            datesOfMonth:
                nextPeriod === ServiceTemplateRecurrencePeriod.MONTHLY
                    ? [1]
                    : [],
            dateOfMonth: null,
            month: null,
            quarterMonth: null,
        });
    };

    const setWeekDays = (weekDays: number[]) => {
        void formik.setFieldValue(
            "defaults.schedule.weekDays",
            [...weekDays].sort((a, b) => a - b),
        );
    };

    const toggleWeekDay = (day: number) => {
        if (schedule.weekDays.includes(day)) {
            setWeekDays(schedule.weekDays.filter((value) => value !== day));
            return;
        }
        setWeekDays([...schedule.weekDays, day]);
    };

    const setDatesOfMonth = (datesOfMonth: number[]) => {
        void formik.setFieldValue(
            "defaults.schedule.datesOfMonth",
            [...datesOfMonth].sort((a, b) => a - b),
        );
    };

    const updateDateOfMonthAt = (index: number, value: number) => {
        const isDuplicate = schedule.datesOfMonth.some(
            (day, i) => day === value && i !== index,
        );
        if (isDuplicate) {
            return;
        }
        const next = [...schedule.datesOfMonth];
        next[index] = value;
        setDatesOfMonth(next);
    };

    const removeDateOfMonthAt = (index: number) => {
        setDatesOfMonth(schedule.datesOfMonth.filter((_, i) => i !== index));
    };

    const addDateOfMonth = () => {
        const used = new Set(schedule.datesOfMonth);
        const nextOption = DAY_OF_MONTH_OPTIONS.find(
            ({ value }) => !used.has(value),
        );
        if (!nextOption) {
            return;
        }
        setDatesOfMonth([...schedule.datesOfMonth, nextOption.value]);
    };

    return (
        <VStack gap={16}>
            <div>
                <label id={startDateLabelId} className={styles.fieldLabel}>
                    Service period start date
                </label>
                <fields.Date
                    name="defaults.schedule.startDate"
                    aria-labelledby={startDateLabelId}
                    buttonProps={{ size: "xl", block: true }}
                    pickerLabel="Service period start date"
                    placeholder="Required"
                />
            </div>

            <div
                className={classNames(
                    recurrenceStyles.block,
                    styles.recurrenceBlock,
                )}
            >
                <div className={recurrenceStyles.line}>
                    <div
                        id={intervalLabelId}
                        className={recurrenceStyles.leftLabel}
                    >
                        Every
                    </div>
                    <div className={recurrenceStyles.fieldContainer}>
                        <fields.Count
                            name="defaults.schedule.interval"
                            aria-labelledby={intervalLabelId}
                            min={1}
                            className={recurrenceStyles.countField}
                        />
                        <Picker.Select
                            id="st-period"
                            items={PERIOD_ITEMS}
                            value={period}
                            onValueChange={changePeriod}
                            renderValue={([selectedPeriod]) =>
                                pluralize(
                                    PERIOD_UNIT_LABELS[selectedPeriod],
                                    schedule.interval,
                                )
                            }
                            trigger={
                                <Picker.Button
                                    variant="primary"
                                    size="xl"
                                    className={recurrenceStyles.periodField}
                                />
                            }
                        />
                    </div>
                </div>
            </div>

            {shouldShowWeekDays ? (
                <HStack gap={4}>
                    {WEEKDAY_TOGGLE_OPTIONS.map(({ value, label }) => {
                        const isSelected = schedule.weekDays.includes(value);
                        return (
                            <button
                                key={value}
                                type="button"
                                className={
                                    isSelected
                                        ? styles.weekdayToggleSelected
                                        : styles.weekdayToggle
                                }
                                onClick={() => toggleWeekDay(value)}
                                aria-pressed={isSelected}
                                aria-label={WEEKDAY_LABELS[value]}
                            >
                                {label}
                            </button>
                        );
                    })}
                </HStack>
            ) : null}

            {shouldShowDatesOfMonth ? (
                <VStack gap={8}>
                    {schedule.datesOfMonth.map((day, index) => (
                        <HStack key={`${day}-${index}`} gap={8} align="center">
                            <DayOfMonthCombobox
                                value={day}
                                onChange={(value) => {
                                    if (value != null) {
                                        updateDateOfMonthAt(index, value);
                                    }
                                }}
                            />
                            <span className={styles.mutedText}>day</span>
                            {schedule.datesOfMonth.length > 1 ? (
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    iconOnly
                                    type="button"
                                    onClick={() => removeDateOfMonthAt(index)}
                                    aria-label="Remove occurrence"
                                >
                                    <CloseIcon />
                                </Button>
                            ) : null}
                        </HStack>
                    ))}
                    <Button
                        variant="secondary"
                        size="sm"
                        leftIcon={<PlusIcon />}
                        type="button"
                        onClick={addDateOfMonth}
                    >
                        Add occurrence
                    </Button>
                </VStack>
            ) : null}

            {shouldShowQuarterly ? (
                <HStack gap={8} align="center">
                    <Picker.Select
                        items={QUARTER_MONTH_OPTIONS}
                        value={schedule.quarterMonth}
                        onValueChange={(value) =>
                            void formik.setFieldValue(
                                "defaults.schedule.quarterMonth",
                                value ?? null,
                            )
                        }
                        placeholder="Month of quarter"
                        trigger={<Picker.Button variant="primary" size="xl" />}
                    />
                    <DayOfMonthCombobox
                        value={schedule.dateOfMonth}
                        placeholder="Day"
                        onChange={(value) =>
                            void formik.setFieldValue(
                                "defaults.schedule.dateOfMonth",
                                value ?? null,
                            )
                        }
                    />
                </HStack>
            ) : null}

            {shouldShowAnnually ? (
                <HStack gap={8} align="center">
                    <DayOfMonthCombobox
                        value={schedule.dateOfMonth}
                        placeholder="Day"
                        onChange={(value) =>
                            void formik.setFieldValue(
                                "defaults.schedule.dateOfMonth",
                                value ?? null,
                            )
                        }
                    />
                    <span className={styles.mutedText}>day of</span>
                    <Picker.Select
                        searchable
                        items={MONTH_OF_YEAR_ITEMS}
                        value={schedule.month}
                        onValueChange={(value) =>
                            void formik.setFieldValue(
                                "defaults.schedule.month",
                                value ?? null,
                            )
                        }
                        placeholder="Select month..."
                        searchPlaceholder="Search months..."
                        emptyLabel="No matching months"
                        trigger={<Picker.Button variant="primary" size="xl" />}
                    />
                </HStack>
            ) : null}
        </VStack>
    );
};
